import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Plus, Home, Sparkles, LogIn, LogOut, ShieldCheck, Presentation } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useAuth } from '../context/AuthContext';
import { Brand, MONO } from './landing/shared';

const NavLink = ({ to, icon: Icon, children, active, accent }) => (
  <Link to={to} className={`inline-flex items-center gap-2 h-9 px-3 rounded-full text-sm transition-colors ${active ? 'bg-white/10 text-white' : accent ? 'text-cyan-300 hover:bg-cyan-500/10' : 'text-slate-400 hover:text-slate-100 hover:bg-white/5'}`}>
    <Icon className="w-4 h-4" /><span className="hidden sm:inline">{children}</span>
  </Link>
);

export function Header() {
  const { user, loading, logout } = useAuth();
  const { pathname } = useLocation();
  const isAdmin = user && (user.role === 'owner' || user.role === 'admin');

  return (
    <motion.header initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="sticky top-0 z-50 border-b border-white/5" style={{ background: 'rgba(8,12,20,0.78)', backdropFilter: 'blur(18px)' }} data-testid="app-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-3">
          <Brand />
          <nav className="flex items-center gap-1">
            <NavLink to="/" icon={Home} active={pathname === '/' || pathname === '/dashboard'}>Dashboard</NavLink>
            <NavLink to="/tools/poster" icon={Presentation} active={pathname.startsWith('/tools/poster')}>Posters</NavLink>
            <NavLink to="/ai-assistant" icon={Sparkles} accent active={pathname === '/ai-assistant'}>AI Assistant</NavLink>
            <NavLink to="/library" icon={FileText} active={pathname === '/library'}>Library</NavLink>
            {isAdmin && <NavLink to="/admin" icon={ShieldCheck} active={pathname === '/admin'}>Admin</NavLink>}
            <Link to="/create" className="ml-1 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-slate-950 text-sm font-semibold shadow-lg shadow-cyan-500/20 active:scale-95 transition-all" data-testid="header-create-btn"><Plus className="w-4 h-4" /><span className="hidden sm:inline">Create</span></Link>

            {loading ? (
              <div className="w-8 h-8 rounded-full bg-slate-700 animate-pulse ml-2" />
            ) : user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2 pl-2 ml-1 rounded-full hover:bg-white/5" data-testid="header-user-menu">
                    <Avatar className="h-7 w-7 ring-2 ring-cyan-500/40">
                      <AvatarImage src={user.picture || ''} alt={user.name || ''} />
                      <AvatarFallback className="bg-cyan-500/20 text-cyan-200 text-xs">{(user.name || user.email || 'U').charAt(0)}</AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline text-sm font-medium text-slate-200">{user.name || user.email?.split('@')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-cyan-400 mt-1" style={{ fontFamily: MONO }}>{user.role}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild><Link to="/library" className="cursor-pointer"><FileText className="mr-2 h-4 w-4" />My Documents</Link></DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-400"><LogOut className="mr-2 h-4 w-4" />Sign Out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" className="ml-1 inline-flex items-center gap-2 h-9 px-4 rounded-full border border-slate-700 text-slate-200 text-sm hover:bg-slate-800/80 transition-colors"><LogIn className="w-4 h-4" /><span className="hidden sm:inline">Sign In</span></Link>
            )}
          </nav>
        </div>
      </div>
    </motion.header>
  );
}
